import { useMemo } from 'react'
import { Cell, Column, SortingRule } from 'react-table'
import { useIntl } from 'react-intl'

import IntlFormattedDate from 'app/i18n/IntlFormattedDate'
import { Mark, Marking } from 'components/Mark'
import { Table } from 'components/Table'
import type { TableHeader } from 'components/Table/types'
import type { JobWithHomeType } from 'features/domains/types'
import {
  getSubscriptionInfo,
  getFullSubscriptionInfo,
} from 'features/domains/utils'
import { sortByInsensitiveString } from 'components/Table/utils'

import {
  JobHostSoftwareVersion,
  JobHostTypeWrapper,
  JobNamesHomeName,
  JobNamesJobName,
  JobNamesWrapper,
} from './styles'
import { DomainJobsTableContainer } from 'features/domains/styles'

import MobileJobHomeComponent from '../DomainMobileJobsComponents/MobileJobHomeComponent'
import { JOBS_HOMES_HEADERS } from '../DomainJobsHomes/consts'
import DomainHomeSubscriptionEndDate from '../DomainHomeSubscriptionEndDate'
//import { DomainsHomeSubscriptionPlan } from '..'
import { DomainsHomeSubscriptionMultiPlan } from '..'
import NotFoundBlock from 'components/NotFoundBlock'
import { makePublicUrl } from 'app/utils/publicUrl'
import { jobsMainMessages } from 'features/jobs/components/JobsMain/messages'
import { commonMessages } from 'app/i18n/messages'
import HostIcon from 'components/HostIcon'

const HomeStatusCell = (cell: Cell<JobWithHomeType>) => {
  const job = cell.row.original
  const isHomeOnline = job.homes[0].online

  return <Mark marking={isHomeOnline ? Marking.Check : Marking.Error} />
}

const JobHomeNameCell = (cell: Cell<JobWithHomeType>) => {
  const job = cell.row.original
  const jobName = job.name
  const home = job.homes[0]
  const homeName = home.name

  return (
    <>
      <JobHostTypeWrapper>
        <HostIcon home={home} />
      </JobHostTypeWrapper>
      <JobNamesWrapper>
        <JobNamesJobName>{jobName}</JobNamesJobName>
        <JobNamesHomeName>
          <b>{homeName}</b>
        </JobNamesHomeName>
      </JobNamesWrapper>
    </>
  )
}

const LastUpdateCell = (cell: Cell<JobWithHomeType>) => {
  const job = cell.row.original
  const updated = job.updated

  return <IntlFormattedDate date={updated} />
}

const SoftwareVersionCell = (cell: Cell<JobWithHomeType>) => {
  const job = cell.row.original
  const softwareVersion = job.homes[0].softwareInfo.version.name

  return <JobHostSoftwareVersion>{softwareVersion}</JobHostSoftwareVersion>
}

const SubscriptionPlanCell = (cell: Cell<JobWithHomeType>) => {
  const job = cell.row.original
  return (
    <DomainsHomeSubscriptionMultiPlan
      subscriptionInfoArray={getFullSubscriptionInfo(job)}
    />
  )
}

const SubscriptionEndCell = (cell: Cell<JobWithHomeType>) => {
  const job = cell.row.original
  const subscriptionInfo = getSubscriptionInfo(job)

  return subscriptionInfo?.expiresAt ? (
    <DomainHomeSubscriptionEndDate subscriptionInfo={subscriptionInfo} />
  ) : (
    '-'
  )
}

const LastBackupCell = (cell: Cell<JobWithHomeType>) => {
  const job = cell.row.original
  const home = job.homes[0]
  const lastBackup = home.lastBackup

  return <IntlFormattedDate date={lastBackup} />
}

interface DomainsJobTableProps {
  data: JobWithHomeType[]
  fetchData: ({
    pageSize,
    pageIndex,
  }: {
    pageSize: number
    pageIndex: number
  }) => void
  headers: Record<JOBS_HOMES_HEADERS, TableHeader>
  defaultSortBy: SortingRule<JobWithHomeType>
  sortBy: SortingRule<JobWithHomeType>
  setSortBy: (sortBy: SortingRule<JobWithHomeType>) => void
  searchValue?: string
  updatePageSize?: (size: number) => void
  updatePageIndex?: (index: number) => void
  pageCount?: number
  itemCount?: number
  loading?: boolean
}

function DomainJobsHomesTable(props: DomainsJobTableProps) {
  const {
    headers,
    data,
    defaultSortBy,
    sortBy,
    setSortBy,
    searchValue,
    pageCount,
    itemCount,
    fetchData,
    loading,
  } = props

  const { formatMessage } = useIntl()

  const customSort = useMemo(
    () => (rowA: any, rowB: any, id: string) => {
      if (rowA.values[id] === rowB.values[id]) {
        return rowA.values[headers.name.id].localeCompare(
          rowB.values[headers.name.id]
        )
      }
      if (rowA.values[id] && !rowB.values[id]) {
        return -1
      }
      if (!rowA.values[id] && rowB.values[id]) {
        return 1
      }
      return 0
    },
    [headers]
  )

  const columns: readonly Column<JobWithHomeType>[] = useMemo(
    () => [
      {
        Header: ' ',
        id: headers.status.id,
        accessor: (row) => row.homes[0].online,
        maxWidth: 50,
        Cell: HomeStatusCell,
        sortType: customSort,
      },
      {
        Header: headers.name.label,
        id: headers.name.id,
        accessor: (row) => row.homes[0].name,
        Cell: JobHomeNameCell,
        percentWidth: '25%',
        sortType: sortByInsensitiveString,
      },
      {
        Header: headers.updated.label,
        id: headers.updated.id,
        accessor: 'updated',
        Cell: LastUpdateCell,
      },
      {
        Header: headers.softwareVersion.label,
        id: headers.softwareVersion.id,
        accessor: (row) => row.homes[0].softwareInfo.version.name,
        Cell: SoftwareVersionCell,
      },
      {
        Header: headers.subscriptionPlan.label,
        id: headers.subscriptionPlan.id,
        Cell: SubscriptionPlanCell,
      },
      {
        Header: headers.subscriptionEnds.label,
        id: headers.subscriptionEnds.id,
        accessor: ({ homes }) =>
          homes[0].subscriptionInfo !== null &&
          homes[0]?.subscriptionInfo?.[0].expiresAt,
        Cell: SubscriptionEndCell,
        disableSortBy: true,
      },
      {
        Header: headers.lastBackup.label,
        id: headers.lastBackup.id,
        accessor: (row) => row.homes[0].lastBackup,
        Cell: LastBackupCell,
      },
    ],
    [headers, customSort]
  )

  const globalFilter = (rows: any, columns: any, filterValue: any) => {
    if (filterValue === null) return rows
    return rows.filter((row: any) => {
      const jobName = row.original.name.toLowerCase()
      const homeName = row.values.name.toLowerCase()

      return (
        jobName.includes(filterValue.toLowerCase()) ||
        homeName.includes(filterValue.toLowerCase())
      )
    })
  }

  return (
    <DomainJobsTableContainer>
      <Table
        $shouldRowBeLink
        columns={columns}
        data={data}
        defaultSortBy={defaultSortBy}
        globalFilter={globalFilter}
        sortBy={sortBy}
        setSortBy={setSortBy}
        searchValue={searchValue}
        mobileViewComponent={MobileJobHomeComponent}
        fetchData={fetchData}
        loading={loading}
        paginatedPageCount={pageCount}
        itemCount={itemCount}
        NoResultsComponent={
          <NotFoundBlock
            imgSrc={makePublicUrl(
              'images/illustrations/homes-illustration.svg'
            )}
            title={formatMessage(jobsMainMessages.noHomesFound)}
            text={formatMessage(commonMessages.fixCriteria)}
          />
        }
      />
    </DomainJobsTableContainer>
  )
}

export default DomainJobsHomesTable
